import { create } from 'zustand';

/**
 * "A run was just asked for."
 *
 * Agent work is polled only while something is already live, so a quiet
 * account never asks the API a question it does not need to. The hole that
 * leaves is the moment a run BEGINS: the poll cannot notice a run it was not
 * watching for, so pressing "Analyse my business" started one and the card
 * beside it went on saying nothing was running until the page was reloaded.
 *
 * Anything that starts a run says so here, and every listener goes and looks.
 * A signal rather than the run itself, because the caller and the card have no
 * relationship worth threading a prop through — they are different sections of
 * different pages that happen to care about the same thing.
 */
interface AgentWorkSignal {
    /** Bumped when a run is created. Only its change matters, not its value. */
    startedAt: number;
    /**
     * Bumped when a run ends.
     *
     * The other half of the same problem. A finished analysis leaves new
     * findings, a fresh screenshot and one fewer setup step behind it, and
     * every surface showing those had already loaded — so the dashboard sat
     * there stale until someone pressed reload. Nobody watching a run finish
     * should have to.
     */
    finishedAt: number;
    /**
     * Which agent profiles are running right now.
     *
     * Published by the one component that polls, so a page can show a spinner
     * on its own button without starting a second poll of its own. Profiles
     * rather than run ids: a page asks "is my kind of work happening", never
     * "is run 47 happening".
     */
    runningProfiles: string[];
    runStarted: () => void;
    runFinished: () => void;
    setRunningProfiles: (profiles: string[]) => void;
}

export const useAgentWorkSignal = create<AgentWorkSignal>((set) => ({
    startedAt: 0,
    finishedAt: 0,
    runningProfiles: [],
    runStarted: () => set({ startedAt: Date.now() }),
    runFinished: () => set({ finishedAt: Date.now() }),
    setRunningProfiles: (profiles) =>
        // Only on a real change: this is written on every poll, and a new array
        // each time would re-render every subscriber a few times a second.
        set((state) =>
            state.runningProfiles.length === profiles.length &&
            state.runningProfiles.every((p, i) => p === profiles[i])
                ? state
                : { runningProfiles: profiles },
        ),
}));

/** For callers outside React, and for the ones that only need to fire it. */
export const signalRunStarted = () => useAgentWorkSignal.getState().runStarted();
export const signalRunFinished = () => useAgentWorkSignal.getState().runFinished();

/** Whether a given kind of agent work is live, for a button that should spin. */
export const useProfileRunning = (profile: string): boolean =>
    useAgentWorkSignal((state) => state.runningProfiles.includes(profile));
